// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: what a dashboard shows while its numbers load, when a panel has nothing in its period
// and when the numbers could not be read: the shared notice card (ui/page_notice), compact inside
// a card and centred in the full screen window; quiet wells where a chart would be; skeletons in
// the shape of the number tiles and charts that are coming, instead of grey blocks and spinners.
// The Lens material's values come from PageNotice's .lens.

import {SkeletonLine} from '@app/features/app/components/skeleton/SkeletonLine';
import styles from '@app/features/konvo_dashboards/components/DashboardNotice.module.css';
import {PageNotice, PageNoticeArea, PageNoticeButton} from '@app/features/ui/page_notice/PageNotice';
import {useLingui} from '@lingui/react/macro';
import {IconAlertTriangleFilled, IconChartBar, IconChartBarOff, IconListDetails} from '@tabler/icons-react';
import {clsx} from 'clsx';
import type React from 'react';

const ICON = {size: 21, stroke: 1.7} as const;

/** The dashboard's numbers could not be read. `card`: compact inside the card on Home or in a
 *  chat; `full`: the scene card centred in the full screen window. */
export function DashboardLoadError({
	place,
	retrying,
	onRetry,
}: {
	place: 'card' | 'full';
	retrying: boolean;
	onRetry: () => void;
}) {
	const {t} = useLingui();
	return (
		<div className={clsx(styles.lens, place === 'card' ? styles.inCard : styles.full)}>
			<PageNoticeArea>
				<PageNotice
					layout={place === 'card' ? 'compact' : 'scene'}
					icon={<IconChartBar {...ICON} />}
					badge={<IconAlertTriangleFilled size={11} />}
					tone="bad"
					title={t({
						message: 'Could not load this dashboard',
						comment: 'Dashboard card: title of the card when its numbers could not be read.',
					})}
					text={t({
						message: 'Konvo could not read the numbers just now. Try again in a moment.',
						comment: 'Dashboard card: text of the card when its numbers could not be read.',
					})}
					actions={
						<div className={styles.actions}>
							<PageNoticeButton ink disabled={retrying} onClick={onRetry} data-flx="konvo-dashboard.retry">
								{t({message: 'Try again', comment: 'Dashboard error card: reads the numbers again.'})}
							</PageNoticeButton>
						</div>
					}
					data-flx="konvo-dashboard.error"
				/>
			</PageNoticeArea>
		</div>
	);
}

/** A quiet well in a panel: nothing in this period (`empty`) or the panel's query failed (`bad`). */
export function PanelWell({
	tone = 'empty',
	text,
	hint,
	tall = true,
	flx,
}: {
	tone?: 'empty' | 'bad';
	text: string;
	hint?: string;
	/** Where a chart would be: as tall as a small chart. Lists and tables keep it short. */
	tall?: boolean;
	flx?: string;
}) {
	return (
		<div className={styles.lens} role={tone === 'bad' ? 'alert' : undefined} data-flx={flx}>
			<div className={clsx(styles.well, tall && styles.wellTall)}>
				<span className={clsx(styles.wellTile, tone === 'bad' && styles.wellTileBad)} aria-hidden>
					{tone === 'bad' ? <IconAlertTriangleFilled size={14} /> : <IconChartBarOff size={16} stroke={1.8} />}
				</span>
				<span className={styles.wellText}>
					<span className={styles.wellTitle}>{text}</span>
					{hint && <span className={styles.wellHint}>{hint}</span>}
				</span>
			</div>
		</div>
	);
}

const BARS = [46, 72, 58, 88, 64, 40, 76, 52, 68, 34, 60, 80];

/** A chart while it draws: bars over a baseline, in a soft well. */
export function ChartSkeleton({height, bars = 9}: {height: number; bars?: number}) {
	return (
		<div className={clsx(styles.lens, styles.chart)} style={{height}} aria-hidden>
			<div className={styles.chartBars}>
				{BARS.slice(0, bars).map((value, index) => (
					<span key={index} style={{height: `${value}%`}} />
				))}
			</div>
		</div>
	);
}

/** A card or the full screen while the numbers load: the number tiles, then charts. */
export function DashboardSkeleton({count, roomy = false}: {count: number; roomy?: boolean}) {
	const {t} = useLingui();
	const charts = roomy ? 2 : Math.min(2, Math.max(1, count - 1));
	return (
		<div
			className={clsx(styles.lens, styles.skeleton, roomy && styles.skeletonRoomy)}
			role="status"
			aria-busy="true"
			aria-label={t({message: 'Loading the dashboard', comment: 'Dashboard card: screen reader label while it loads.'})}
			data-flx="konvo-dashboard.loading"
		>
			<div className={styles.tiles} aria-hidden>
				{[0, 1, 2].map((index) => (
					<div key={index} className={styles.tile}>
						<SkeletonLine width={['46%', '58%', '38%'][index]} height="10px" />
						<SkeletonLine width="64px" height={roomy ? '26px' : '22px'} />
						<SkeletonLine width="70%" height="9px" />
					</div>
				))}
			</div>
			<div className={clsx(styles.charts, roomy && styles.chartsRoomy)} aria-hidden>
				{Array.from({length: charts}, (_, index) => (
					<div key={index} className={styles.panel}>
						<div className={styles.panelHead}>
							<SkeletonLine width={index === 0 ? '34%' : '42%'} height="12px" />
							<SkeletonLine width="22%" height="9px" />
						</div>
						<ChartSkeleton height={roomy ? 260 : 170} bars={roomy ? 12 : 9} />
					</div>
				))}
			</div>
		</div>
	);
}

/** Drill-down records while they load: rows shaped like its list. */
export function DrillSkeleton() {
	const {t} = useLingui();
	return (
		<div
			className={clsx(styles.lens, styles.rows)}
			role="status"
			aria-busy="true"
			aria-label={t({
				message: 'Loading the records',
				comment: 'Dashboard drill-down: screen reader label while it loads.',
			})}
		>
			{['62%', '48%', '70%', '54%', '40%'].map((width) => (
				<div key={width} className={styles.row} aria-hidden>
					<span className={styles.rowText}>
						<SkeletonLine width={width} height="12px" />
						<SkeletonLine width="32%" height="9px" />
					</span>
					<SkeletonLine width="48px" height="10px" />
				</div>
			))}
		</div>
	);
}

/** Settings → Dashboards while the list loads: rows shaped like its settings rows. */
export function SavedListSkeleton() {
	const {t} = useLingui();
	return (
		<div
			className={clsx(styles.lens, styles.rows)}
			role="status"
			aria-busy="true"
			aria-label={t({
				message: 'Loading your dashboards',
				comment: 'Settings dashboards: screen reader label while the list loads.',
			})}
		>
			{['44%', '56%'].map((width) => (
				<div key={width} className={styles.row} aria-hidden>
					<SkeletonLine width="32px" height="32px" />
					<span className={styles.rowText}>
						<SkeletonLine width={width} height="12px" />
						<SkeletonLine width="30%" height="9px" />
					</span>
				</div>
			))}
		</div>
	);
}

/** A notice in a dialog or settings list: compact, with the tile beside the title. */
export function DashboardCompactNotice({
	tone = 'plain',
	title,
	text,
	list = false,
	actions,
	flx,
}: {
	tone?: 'plain' | 'bad';
	title: string;
	text?: string;
	list?: boolean;
	actions?: React.ReactNode;
	flx?: string;
}) {
	return (
		<div className={clsx(styles.lens, styles.inCard, styles.flush)}>
			<PageNoticeArea inline>
				<PageNotice
					layout="compact"
					icon={list ? <IconListDetails {...ICON} /> : <IconChartBar {...ICON} />}
					badge={tone === 'bad' ? <IconAlertTriangleFilled size={11} /> : undefined}
					tone={tone}
					title={title}
					text={text}
					actions={actions}
					data-flx={flx}
				/>
			</PageNoticeArea>
		</div>
	);
}
